import { useContext, useRef } from 'react';
import { FontAwesomeIcon } from '@/components/Common/FontAwesomeIcon/FontAwesomeIcon';
import { useApplyCoupon, useCancelCoupon } from '@/api/coupun';
import { CheckoutContext } from '@/context/CheckoutContext';
import { cn } from '@/utils/cn';
import { Tag } from '@/components/Common/Tag';
import type { PriceBreakdown } from '@/types/checkout';
import SuccessIcon from '@/assets/icons/common/success.svg';
import ErrorIcon from '@/assets/icons/common/error.svg';
import { useTranslate } from '@/hooks/useTranslate';
import { mixpanelService } from '@/services/mixpanelService';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { getErrorMessage } from '@/utils/error';
import { useIsMobile } from '@/hooks/useIsMobile';
import { useDetailsContentColor, useHasDetailsBackground } from '@/hooks/useDetailsContentColor';
import { isApplePayTabMode } from '@/utils/location';
import { getPromoCodeErrorMessageKey, initialPromoCodeState, normalizePromoCode } from '@/utils/promoCode';
import { PromoCodeTrigger } from '@/components/PromoCode/PromoCodeTrigger';
import { FeatureFlag } from '@/constants/featureFlags';

const calculateDiscountPercentage = (discountAmount: number, totalAmount: number) => {
  const positiveDiscount = Math.abs(discountAmount);
  return (positiveDiscount / totalAmount) * 100;
};

export const PromoCode = () => {
  const context = useContext(CheckoutContext);
  const {
    boot: { couponsConfiguration },
    promoCodeBreakdown,
    featureFlags,
    actions: { updatePriceBreakdown, updatePromoCodeBreakdown },
  } = context;
  const { promoCodeName, expanded, error, success } = promoCodeBreakdown;
  const forceUpperCase = featureFlags?.[FeatureFlag.CHECKOUT_PROMO_FORCE_UPPER_CASE] === true;
  const detailsColorFromHook = useDetailsContentColor();
  const { mutateAsync: applyCoupon, isPending } = useApplyCoupon();
  const { mutateAsync: cancelCoupon, isPending: isCancelPending } = useCancelCoupon();
  const { t } = useTranslate();
  const isMobile = useIsMobile();
  const hasBackground = useHasDetailsBackground();
  const desktopWithBackgroundImage = !isMobile && hasBackground;
  const applyRequestIdRef = useRef(0);

  const invalidateApplyRequest = () => {
    applyRequestIdRef.current += 1;
  };

  const handleApplyCoupon = async () => {
    const appliedCode = normalizePromoCode(promoCodeName.trim(), forceUpperCase);
    if (!appliedCode) return;

    const requestId = ++applyRequestIdRef.current;
    try {
      const priceBreakdown = await applyCoupon(appliedCode);
      if (requestId !== applyRequestIdRef.current) return;

      updatePriceBreakdown(priceBreakdown);
      const discountPercentage = calculateDiscountPercentage(priceBreakdown?.discount, priceBreakdown?.baseNet);
      updatePromoCodeBreakdown({
        promoCodeName: appliedCode,
        expanded: true,
        error: '',
        success: true,
        discountAmount: priceBreakdown?.discount,
        discountPercentage,
      });

      const properties = getMixpanelProperties(context, {
        promo_code: appliedCode,
      });
      mixpanelService.track(MixpanelEvents.COUPON_APPLIED, properties as Record<string, unknown>);
    } catch (applyError) {
      if (requestId !== applyRequestIdRef.current) return;

      updatePromoCodeBreakdown({
        success: false,
        error: getErrorMessage(applyError, t('couponDoesNotExist')),
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updatePromoCodeBreakdown({ promoCodeName: normalizePromoCode(e.target.value, forceUpperCase), error: '' });
  };

  const handleClearErrorCoupon = () => {
    invalidateApplyRequest();
    updatePromoCodeBreakdown({
      ...initialPromoCodeState(),
      expanded: true,
    });
  };

  const handleRemoveCoupon = async () => {
    try {
      if (isCancelPending) return;
      const priceBreakdown = (await cancelCoupon()) as PriceBreakdown;
      updatePriceBreakdown(priceBreakdown);

      const properties = getMixpanelProperties(context, {
        promo_code: promoCodeName || undefined,
      });
      mixpanelService.track(MixpanelEvents.PROMO_CODE_REMOVED, properties as Record<string, unknown>);

      updatePromoCodeBreakdown({
        ...initialPromoCodeState(),
        expanded: true,
      });
    } catch {
      updatePromoCodeBreakdown({ error: t('failedToRemovePromoCode') });
    }
  };

  // Apple Pay fallback tab: intentionally a bare review-and-pay surface —
  // no coupon entry (applied promos live in the original framed checkout).
  const applePayTabMode = isApplePayTabMode();

  const handleCancel = () => {
    invalidateApplyRequest();
    updatePromoCodeBreakdown(initialPromoCodeState());
  };

  // Subscriptions have no shopper-entered coupon: `apply-promo-code` lives on
  // the checkout session store, which a subscription token can't address, and
  // a plan's discount arrives ready-made on the summary. v2 excludes the field
  // the same way (`<Description isSubscription />`).
  if (!couponsConfiguration?.enabled || context.subscription) {
    return null;
  }

  const collapsedContainerClass = cn(
    'w-full relative md:rounded-md px-4 md:px-0 py-4 landscape:py-0 md:py-3 md:pl-0 border-1 border-y-sidebar-border border-x-transparent md:border-none',
    desktopWithBackgroundImage || !isMobile ? 'bg-transparent' : 'bg-light-gray'
  );

  if (!expanded && !success && !applePayTabMode) {
    return (
      <PromoCodeTrigger
        containerClassName={collapsedContainerClass}
        onExpand={() => updatePromoCodeBreakdown({ expanded: true })}
      />
    );
  }

  return (
    <>
      {!applePayTabMode && (
        <div data-testid="promo-code-section" className={collapsedContainerClass} style={detailsColorFromHook}>
          <div className="mb-3 flex items-center justify-between text-sm font-semibold">
            <span className="text-card-foreground cursor-default" style={!isMobile ? detailsColorFromHook : undefined}>
              {t('addPromoCode')}
            </span>
            {!success && (
              <button
                type="button"
                data-testid="promo-code-cancel"
                onClick={handleCancel}
                disabled={isPending}
                className={cn('text-muted-foreground', isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer')}
                style={!isMobile ? detailsColorFromHook : undefined}
              >
                {t('cancel')}
              </button>
            )}
          </div>
          <div
            className={cn(
              'flex items-center rounded-md border-1 border-sidebar-border bg-white transition-all duration-200',
              success && 'border-success focus-within:border-success',
              error && 'border-danger focus-within:border-danger'
            )}
          >
            <input
              type="text"
              data-testid="promo-code-input"
              placeholder={t('promoCode')}
              value={promoCodeName}
              onChange={handleInputChange}
              className={cn(
                // `min-w-0` lets a long code shrink/clip inside the flex row so
                // it never paints over Apply / Remove / error actions.
                'min-w-0 flex-1 h-10 appearance-none rounded-md bg-transparent p-2 pl-3 text-base font-family-sans text-card-foreground outline-0',
                // Paired with the value transform so a paste/autofill never
                // flashes lowercase before React re-renders. `text-transform`
                // would hit the placeholder too, so the label stays as authored.
                forceUpperCase && 'uppercase placeholder:normal-case'
              )}
              disabled={isPending || success}
            />
            <div
              data-testid="promo-code-input-actions"
              className="flex shrink-0 items-center gap-2 pr-4"
            >
              <PromoCodeInputActions
                success={success}
                error={error}
                promoCode={promoCodeName}
                isPending={isPending}
                isMobile={isMobile}
                detailsColor={detailsColorFromHook}
                onRemove={handleRemoveCoupon}
                onClearError={handleClearErrorCoupon}
                onApply={handleApplyCoupon}
                t={t}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

type PromoCodeInputActionsProps = {
  success: boolean;
  error: string;
  promoCode: string;
  isPending: boolean;
  isMobile: boolean;
  detailsColor: React.CSSProperties | undefined;
  onRemove: () => void;
  onClearError: () => void;
  onApply: () => void;
  t: (key: string) => string;
};

const PromoCodeInputActions = ({
  success,
  error,
  promoCode,
  isPending,
  isMobile,
  detailsColor,
  onRemove,
  onClearError,
  onApply,
  t,
}: PromoCodeInputActionsProps) => {
  if (success) {
    return (
      <>
        <Tag text={t('remove')} onClick={onRemove} />
        <img src={SuccessIcon} className="h-5 w-5" />
      </>
    );
  }

  if (error) {
    return (
      <>
        <Tag text={t(getPromoCodeErrorMessageKey(error))} />
        <img src={ErrorIcon} className="h-5 w-5 cursor-pointer" onClick={onClearError} />
      </>
    );
  }

  if (promoCode.trim()) {
    return (
      <button
        onClick={onApply}
        disabled={isPending}
        className={cn(
          'text-sm font-medium text-blue transition-opacity duration-200',
          isPending ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        )}
      >
        {t('apply')}
      </button>
    );
  }

  return (
    <FontAwesomeIcon
      icon="fa-regular fa-tag"
      className="text-muted-foreground"
      style={!isMobile ? detailsColor : undefined}
    />
  );
};
